// ────────────────────────────────────────────────────────────────
// src/pages/ScheduledPaymentsPage.js
// List all schedules & allow cancel / edit
//
// • Visual overhaul to match SocialPage card / table style:
//     – wrapped in card container (border, shadow, rounded)
//     – text-xs base w/ md:text-sm up-scaling
//     – uppercase thead
//     – consistent spacing (px-2 / md:px-4, py-2 / md:py-3)
// ────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import axios from 'axios'

function ScheduledPaymentsPage() {
  /* ───────────────────────────
     Auth / language
  ─────────────────────────── */
  const nav   = useNavigate()
  const { lang } = useOutletContext() || {}
  const token = localStorage.getItem('token')

  const i18n={
    en:{ title:'Scheduled Payments', new:'New Schedule',
         recipient:'Recipient', amount:'Amount',
         freq:'Freq', next:'Next Run', status:'Status', actions:'Actions',
         weekly:'wk', monthly:'mo', yearly:'yr',
         edit:'Edit', del:'Cancel', confirm:'Are you sure?',
         none:'No scheduled payments yet. Click “New Schedule” to add one.',
         currency:'¥' },
    ja:{ title:'定期支払い', new:'新規スケジュール',
         recipient:'受取人', amount:'金額',
         freq:'頻度', next:'次回', status:'状態', actions:'操作',
         weekly:'週次', monthly:'月次', yearly:'年次',
         edit:'編集', del:'キャンセル', confirm:'よろしいですか？',
         none:'まだスケジュールはありません。「新規スケジュール」をクリックしてください。',
         currency:'¥' },
    pt:{ title:'Pagamentos Agendados', new:'Novo Agendamento',
         recipient:'Destinatário', amount:'Valor',
         freq:'Freq', next:'Próx', status:'Status', actions:'Ações',
         weekly:'sem', monthly:'mês', yearly:'ano',
         edit:'Editar', del:'Cancelar', confirm:'Tem certeza?',
         none:'Nenhum pagamento agendado. Clique em “Novo Agendamento” para adicionar.',
         currency:'¥' },
  }
  const t=i18n[lang]||i18n.en

  /* ───────────────────────────
     State & fetch
  ─────────────────────────── */
  const [items,setItems]=useState([])

  useEffect(()=>{
    axios.get(
      'https://payme-backend-713827320947.asia-northeast1.run.app/api/schedule/list',
      { headers:{ Authorization:`Bearer ${token}` }})
      .then(r=>setItems(Array.isArray(r.data)?r.data:[]))
      .catch(e=>console.error(e))
  },[token])

  /* ───────────────────────────
     Handlers
  ─────────────────────────── */
  const cancelSched = async(id)=>{
    if(!window.confirm(t.confirm)) return
    try{
      await axios.delete(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/schedule/${id}`,
        { headers:{ Authorization:`Bearer ${token}` }})
      setItems(items.filter(i=>i.id!==id))
    }catch(err){ console.error(err) }
  }

  /* ───────────────────────────
     Render
  ─────────────────────────── */
  return (
    <div className="max-w-screen-lg mx-auto px-2 py-4 space-y-4">
      {/* ───────────── Card ───────────── */}
      <div className="card bg-white border border-gray-200 rounded shadow-sm text-sm">
        {/* Header */}
        <div className="card-header border-b border-dashed p-4 flex items-center justify-between">
          <h5 className="text-base md:text-lg font-semibold text-gray-700">
            {t.title}
          </h5>
          <button
            onClick={()=>nav('/schedule/new')}
            className="bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700 text-sm"
          >
            {t.new}
          </button>
        </div>

        {/* Body */}
        <div className="card-body p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-gray-600 text-xs md:text-sm">
              <thead className="uppercase bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-2 md:px-4 py-2 md:py-3">{t.recipient}</th>
                  <th className="px-2 md:px-4 py-2 md:py-3">{t.amount}</th>
                  <th className="px-2 md:px-4 py-2 md:py-3">{t.freq}</th>
                  <th className="px-2 md:px-4 py-2 md:py-3">{t.next}</th>
                  <th className="px-2 md:px-4 py-2 md:py-3">{t.status}</th>
                  <th className="px-2 md:px-4 py-2 md:py-3">{t.actions}</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {items.map(it=>(
                  <tr key={it.id} className="hover:bg-gray-50">
                    <td className="px-2 md:px-4 py-2 md:py-3">
                      {it.to_user_name}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3">
                      {t.currency}{it.amount}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3">
                      {it.freq==='weekly'
                        ? t.weekly
                        : it.freq==='monthly'
                        ? t.monthly
                        : t.yearly}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3">
                      {new Date(it.next_run_at).toLocaleDateString()}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3">
                      {it.status}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap">
                      <Link
                        className="text-blue-600 hover:underline"
                        to={`/schedule/edit/${it.id}`}
                      >
                        {t.edit}
                      </Link>
                      {it.status!=='cancelled' && (
                        <button
                          className="text-red-600 hover:underline ml-3"
                          onClick={()=>cancelSched(it.id)}
                        >
                          {t.del}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Empty-state */}
            {items.length===0 && (
              <p className="mt-4 text-gray-500 text-center">
                {t.none}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScheduledPaymentsPage
// ────────────────────────────────────────────────────────────────
// END – ScheduledPaymentsPage.js  (no logic removed)
// ────────────────────────────────────────────────────────────────
